import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Calendar, Tag, ArrowLeft, Share2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { formatContent } from '../utils/markdownParser';
import { PostContent } from '../components/PostContent';
import { SEO } from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function PostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    try {
      const response = await axios.get(`${API}/posts/${slug}`);
      setPost(response.data);
    } catch (error) {
      setError('Post not found');
    } finally {
      setLoading(false);
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: post.title,
          url: window.location.href
        });
      } catch (error) {
        if (error.name !== 'AbortError') {
          copyToClipboard();
        }
      }
    } else {
      copyToClipboard();
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success('Link copied to clipboard!');
  };

  if (loading) {
    return (
      <div className="max-w-[900px] mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-3/4" />
          <div className="h-4 bg-muted rounded w-1/4" />
          <div className="aspect-video bg-muted rounded-lg" />
          <div className="space-y-2">
            <div className="h-4 bg-muted rounded" />
            <div className="h-4 bg-muted rounded" />
            <div className="h-4 bg-muted rounded w-5/6" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="max-w-[900px] mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
        <h1 className="text-2xl font-bold mb-4">Post Not Found</h1>
        <p className="text-muted-foreground mb-8">
          The post you're looking for doesn't exist or has been removed.
        </p>
        <Link to="/">
          <Button>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Home
          </Button>
        </Link>
      </div>
    );
  }

  const formattedDate = new Date(post.created_at).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const updatedDate = post.updated_at ? new Date(post.updated_at) : null;
  const showUpdated =
    updatedDate &&
    !isNaN(updatedDate) &&
    updatedDate.toDateString() !== new Date(post.created_at).toDateString();
  const formattedUpdated = showUpdated
    ? updatedDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    : null;

  const resolveImageUrl = (img) => {
    if (!img) return '';
    return img.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${img}` : img;
  };

  return (
    <article className="max-w-[900px] mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="post-page">
      <SEO
        title={post.title}
        description={post.excerpt || post.content.substring(0, 160)}
        image={resolveImageUrl(post.featured_image)}
        url={`/post/${post.slug}`}
        type="article"
        article={{
          publishedTime: post.created_at,
          modifiedTime: post.updated_at,
          tags: post.tags,
          section: post.category
        }}
      />
      {/* Back Link */}
      <Link 
        to="/" 
        className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-8 transition-colors"
        data-testid="back-to-home"
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to all posts
      </Link>

      {/* Header */}
      <header className="mb-8 animate-fade-in">
        {(post.categories?.length > 0 || post.category) && (
          <div className="flex flex-wrap gap-1.5 mb-4">
            {(post.categories?.length ? post.categories : [post.category]).filter(Boolean).map((cat) => (
              <Badge key={cat} variant="secondary">
                <Tag className="h-3 w-3 mr-1" />
                {cat}
              </Badge>
            ))}
          </div>
        )}
        
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter mb-4">
          {post.title}
        </h1>
        
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center text-sm text-muted-foreground">
            <Calendar className="h-4 w-4 mr-2" />
            <time dateTime={post.created_at}>{formattedDate}</time>
            {showUpdated && (
              <span className="ml-3 italic" data-testid="post-updated">
                Updated {formattedUpdated}
              </span>
            )}
          </div>
          
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={handleShare}
            data-testid="share-btn"
          >
            <Share2 className="h-4 w-4 mr-2" />
            Share
          </Button>
        </div>
      </header>

      {/* Featured Image */}
      {post.featured_image && (
        <div className="mb-8 rounded-lg overflow-hidden animate-fade-in-delay-1">
          <img
            src={post.featured_image.startsWith('/api') 
              ? `${process.env.REACT_APP_BACKEND_URL}${post.featured_image}` 
              : post.featured_image}
            alt={post.title}
            className="w-full h-auto"
          />
        </div>
      )}

      {/* Content */}
      <PostContent
        className="prose max-w-none animate-fade-in-delay-2"
        html={formatContent(post.content)}
        testId="post-content"
      />

      {/* Tags */}
      {post.tags && post.tags.length > 0 && (
        <div className="mt-8 pt-8 border-t border-border">
          <h3 className="text-sm font-medium mb-3">Tags</h3>
          <div className="flex flex-wrap gap-2">
            {post.tags.map((tag) => (
              <Badge key={tag} variant="outline">
                {tag}
              </Badge>
            ))}
          </div>
        </div>
      )}
    </article>
  );
}
